import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert, Modal } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import SalesProfileFormFields from '../components/SalesProfileFormFields';
import '../App.css';

const EMPTY_FORM = { name: '', solution_category: '', offering_description: '' };

const load_profiles = async (token, set_profiles, set_loading, set_error) => {
	set_loading(true);
	const user_id = localStorage.getItem(Constants.USER_ID);
	try {
		const headers = token ? { Authorization: 'Bearer ' + token } : {};
		const response = await axios.get(Constants.SERVER_URL + '/api/sales_profiles', { headers });
		set_profiles(response.data.profiles || []);
	} catch (error) {
		set_error('Failed to load sales profiles.');
	} finally {
		set_loading(false);
	}
};

function SalesProfilesView() {
	const { token } = useAuth();

	const [profiles, set_profiles] = useState([]);
	const [loading, set_loading] = useState(false);
	const [error, set_error] = useState('');

	const [show_modal, set_show_modal] = useState(false);
	const [editing_id, set_editing_id] = useState(null);
	const [form, set_form] = useState(EMPTY_FORM);
	const [modal_loading, set_modal_loading] = useState(false);
	const [modal_saving, set_modal_saving] = useState(false);
	const [modal_error, set_modal_error] = useState('');

	const [confirm_delete, set_confirm_delete] = useState(null);
	const [deleting, set_deleting] = useState(false);

	useEffect(() => {
		load_profiles(token, set_profiles, set_loading, set_error);
	}, [token]);

	const handle_new = () => {
		set_editing_id(null);
		set_form(EMPTY_FORM);
		set_modal_error('');
		set_modal_loading(false);
		set_show_modal(true);
	};

	const handle_edit = async (profile) => {
		set_editing_id(profile.id);
		set_form(EMPTY_FORM);
		set_modal_error('');
		set_modal_loading(true);
		set_show_modal(true);
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const response = await axios.get(
				Constants.SERVER_URL + '/api/sales_profiles/' + profile.id,
				{ headers }
			);
			const p = response.data.profile;
			set_form({ name: p.name, solution_category: p.solution_category, offering_description: p.offering_description });
		} catch (error) {
			set_modal_error('Failed to load profile.');
		} finally {
			set_modal_loading(false);
		}
	};

	const handle_change = (field, value) => {
		set_form(prev => Object.assign({}, prev, { [field]: value }));
	};

	const handle_save = async () => {
		set_modal_saving(true);
		set_modal_error('');
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			const payload = Object.assign({}, form);
			if(editing_id === null) {
				await axios.post(Constants.SERVER_URL + '/api/sales_profiles', payload, { headers });
			} else {
				await axios.put(Constants.SERVER_URL + '/api/sales_profiles/' + editing_id, payload, { headers });
			}
			set_show_modal(false);
			load_profiles(token, set_profiles, set_loading, set_error);
		} catch (error) {
			set_modal_error('Failed to save profile.');
		} finally {
			set_modal_saving(false);
		}
	};

	const handle_hide = () => {
		if(modal_saving) return;
		set_show_modal(false);
	};

	const handle_delete_click = (e, profile) => {
		e.stopPropagation();
		set_confirm_delete(profile);
	};

	const handle_confirm_delete = async () => {
		set_deleting(true);
		try {
			const headers = token ? { Authorization: 'Bearer ' + token } : {};
			await axios.delete(
				Constants.SERVER_URL + '/api/sales_profiles/' + confirm_delete.id,
				{ headers }
			);
			set_confirm_delete(null);
			load_profiles(token, set_profiles, set_loading, set_error);
		} catch (error) {
			set_error('Failed to delete profile.');
			set_confirm_delete(null);
		} finally {
			set_deleting(false);
		}
	};

	const can_save = form.name.trim().length > 0
		&& form.solution_category.trim().length > 0
		&& form.offering_description.trim().length > 0;

	const is_new = editing_id === null;

	return (
		<React.Fragment>
			<main className="sp-profiles-main">
				<div className="sp-profiles-header">
					<div>
						<h4 className="sp-profiles-title">Sales Profiles</h4>
						<p className="sp-profiles-sub-title-text">Create or choose a product or service to sell</p>
					</div>
					<Button variant="primary" size="sm" onClick={handle_new}>
						New
					</Button>
				</div>

				{error ? <Alert variant="danger">{error}</Alert> : null}
				{loading ? (
					<div className="sp-profiles-loading"><Spinner animation="border" size="sm" /></div>
				) : profiles.length === 0 ? (
					<p className="sp-profiles-empty">No sales profiles yet. Create one to get started.</p>
				) : (
					<div className="sp-profiles-grid">
						{profiles.map(profile => (
							<div
								key={profile.id}
								className="sp-profile-card"
								onClick={() => handle_edit(profile)}>
								<button
									className="sp-profile-card-delete"
									onClick={(e) => handle_delete_click(e, profile)}
									title="Delete">
									<i className="fas fa-xmark"></i>
								</button>
								<div className="sp-profile-card-name">{profile.name}</div>
								<div className="sp-profile-card-category">{profile.solution_category}</div>
							</div>
						))}
					</div>
				)}
			</main>

			<Modal
				show={show_modal}
				onHide={handle_hide}
				centered
				dialogClassName="sp-modal-dialog"
				backdrop="static">
				<Modal.Header closeButton>
					<Modal.Title>{is_new ? 'New Sales Profile' : 'Edit Sales Profile'}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="sp-modal-body">
					{modal_error ? <Alert variant="danger" className="sp-modal-alert">{modal_error}</Alert> : null}
					{modal_loading ? (
						<div className="sp-modal-loading"><Spinner animation="border" /></div>
					) : (
						<div className="sp-modal-content">
							<SalesProfileFormFields form={form} on_change={handle_change} class_prefix="sp-modal" />
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button variant="outline-secondary" onClick={handle_hide} disabled={modal_saving}>
						Cancel
					</Button>
					<Button variant="primary" onClick={handle_save} disabled={!can_save || modal_saving || modal_loading}>
						{modal_saving ? <Spinner animation="border" size="sm" /> : 'Save'}
					</Button>
				</Modal.Footer>
			</Modal>

			<Modal
				show={!!confirm_delete}
				onHide={() => { if(!deleting) set_confirm_delete(null); }}
				centered
				size="sm">
				<Modal.Header closeButton>
					<Modal.Title>Delete Profile</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p>Delete <strong>{confirm_delete ? confirm_delete.name : ''}</strong>?</p>
					<p>This cannot be undone.</p>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" onClick={() => set_confirm_delete(null)} disabled={deleting}>
						Cancel
					</Button>
					<Button variant="danger" onClick={handle_confirm_delete} disabled={deleting}>
						{deleting ? <Spinner animation="border" size="sm" /> : 'Delete'}
					</Button>
				</Modal.Footer>
			</Modal>
		</React.Fragment>
	);
}

export default SalesProfilesView;
